import { useDispatch } from 'react-redux';
import MojitoServices from 'mojito/services';
import MojitoPresentation from 'mojito/presentation';
import LoginTypes from 'modules/login/types.js';
import { actions as loginActions } from 'modules/login/slice.js';
import { omit } from 'mojito/utils';

const AnimatedButton = MojitoPresentation.Components.AnimatedButton.component;
const AnimatedButtonTypes = MojitoPresentation.Components.AnimatedButton.types;
const ANIMATED_BUTTON_STATES = AnimatedButtonTypes.STATES;
const { LOGGED_IN } = MojitoServices.Authentication.types.STATES;
const { Text, FlexPane } = MojitoPresentation.Components;
const { CurrencyHelper } = MojitoPresentation.Utils;
const { BETSLIP_STATUS, COMMIT_BETSLIP_MODE } = MojitoServices.Betslip.types;
const { PLACE_BET, SAVE_BET, SHARE_BET } = COMMIT_BETSLIP_MODE;
const { PENDING_PLACE, PENDING_SAVE, PENDING_SHARE, UPDATING } = BETSLIP_STATUS;
const { Image, TimedTooltip } = MojitoPresentation.Components;

const BUTTON_STATE_CLASSNAMES = {
    ACTIVE: 'ACTIVE',
    DISABLED: 'DISABLED',
    IS_BETSLIP_PENDING: 'IS_BETSLIP_PENDING',
    HAS_CHANGES: 'HAS_CHANGES',
    IS_LOGGED_OUT: 'IS_LOGGED_OUT',
    HAS_SUSPENDED_SELECTIONS: 'HAS_SUSPENDED_SELECTIONS',
    HAS_BET_SPECIFIC_ERRORS: 'HAS_BET_SPECIFIC_ERRORS',
};

const betslipModeToButtonTextMap = {
    [PLACE_BET]: '$BETSLIP.PLACE_BET',
    [SAVE_BET]: '$BETSLIP.SAVE_BET',
    [SHARE_BET]: '$BETSLIP.SHARE_BET',
};

const betslipModeToPendingButtonTextMap = {
    [PLACE_BET]: '$BETSLIP.PLACING_BET',
    [SAVE_BET]: '$BETSLIP.SAVING_BET',
    [SHARE_BET]: '$BETSLIP.SHARING_BET',
};

const CommitBetslipButton = ({
    calculation,
    isSuspended,
    currencyCode,
    betslipStatus,
    hasBetslipChanges,
    disabled,
    showTooltip,
    onCommit,
    onAcceptPriceChanges,
    onCloseTooltip,
    commitBetslipMode,
    affiliateId,
    class: className,
    mojitoTools: { config, stringResolver, appContext, style },
}) => {
    const isShareBetMode = commitBetslipMode === SHARE_BET;
    const isPlaceBetMode = commitBetslipMode === PLACE_BET;
    const isSaveBetMode = commitBetslipMode === SAVE_BET;
    const isPendingPlace = betslipStatus === PENDING_PLACE && isPlaceBetMode;
    const isPendingSave = betslipStatus === PENDING_SAVE && isSaveBetMode;
    const isPendingShare = betslipStatus === PENDING_SHARE && isShareBetMode;
    const dispatch = useDispatch();

    if (config.hide) {
        return null;
    }
    const showLoginView = () => {
        appContext.analyticsEmitter.emitAnalytics('openLogin', 'Betslip');
        dispatch(loginActions.showLoginView({ loginReason: LoginTypes.LOGIN_REASON.PLACE_BET }));
    };

    const showPotentialReturns = buttonState => {
        const { ACTIVE, DISABLED } = ANIMATED_BUTTON_STATES;
        return config.showPotentialReturn && (buttonState === ACTIVE || buttonState === DISABLED);
    };

    const getButtonAttrs = () => {
        let onClick = onCommit;
        let buttonTextKey = betslipModeToButtonTextMap[commitBetslipMode];
        let buttonState = ANIMATED_BUTTON_STATES.DISABLED;
        let classNameSuffix = BUTTON_STATE_CLASSNAMES.DISABLED;

        const { stake = 0 } = calculation;
        if (stake > 0 || isShareBetMode) {
            buttonState = ANIMATED_BUTTON_STATES.ACTIVE;
            classNameSuffix = BUTTON_STATE_CLASSNAMES.ACTIVE;
        }

        if (hasBetslipChanges) {
            onClick = onAcceptPriceChanges;
            buttonState = ANIMATED_BUTTON_STATES.CONFIRM;
            buttonTextKey = '$BETSLIP.ACCEPT_CHANGE';
            classNameSuffix = BUTTON_STATE_CLASSNAMES.HAS_CHANGES;
        } else if (isPlaceBetMode && appContext.getLoginState() !== LOGGED_IN) {
            onClick = showLoginView;
            buttonTextKey = '$BETSLIP.LOGIN_TO_PLACE_BET';
            classNameSuffix = BUTTON_STATE_CLASSNAMES.IS_LOGGED_OUT;
            buttonState = ANIMATED_BUTTON_STATES.ACTIVE;
        } else if (disabled) {
            buttonState = ANIMATED_BUTTON_STATES.DISABLED;
            classNameSuffix = BUTTON_STATE_CLASSNAMES.DISABLED;
        } else if (isPendingPlace || isPendingSave || isPendingShare) {
            buttonTextKey = betslipModeToPendingButtonTextMap[commitBetslipMode];
            buttonState = ANIMATED_BUTTON_STATES.ANIMATING;
            classNameSuffix = BUTTON_STATE_CLASSNAMES.IS_BETSLIP_PENDING;
        } else if (betslipStatus === UPDATING) {
            buttonTextKey = '$BETSLIP.UPDATING_BET';
            buttonState = ANIMATED_BUTTON_STATES.ANIMATING;
            classNameSuffix = BUTTON_STATE_CLASSNAMES.IS_BETSLIP_PENDING;
            onClick = null;
        } else if (isSuspended) {
            buttonState = ANIMATED_BUTTON_STATES.DISABLED;
            classNameSuffix = BUTTON_STATE_CLASSNAMES.HAS_SUSPENDED_SELECTIONS;
        }

        return {
            onClick,
            buttonState,
            classNameSuffix,
            buttonText: stringResolver.resolveString(buttonTextKey),
            showStake: buttonTextKey === '$BETSLIP.PLACE_BET' && config.showTotalStake,
        };
    };

    const { onClick, buttonState, buttonText, classNameSuffix, showStake } = getButtonAttrs();

    const getButtonTextStyles = () => {
        if (buttonState === ANIMATED_BUTTON_STATES.DISABLED) {
            return {
                buttonTextConfig: style.buttonTextDisabled,
                totalStakeTextConfig: style.totalStakeTextDisabled,
                additionalInfoLabel: style.additionalInfoDisabled,
            };
        }
        return {
            buttonTextConfig: config.buttonText,
            totalStakeTextConfig: config.totalStakeText,
            additionalInfoLabel: config.additionalInfoLabel,
        };
    };

    const { buttonTextConfig, totalStakeTextConfig, additionalInfoLabel } = getButtonTextStyles();

    const renderAdditionalInfo = () => {
        if (isPlaceBetMode && showPotentialReturns(buttonState)) {
            const { payoutNet } = calculation;
            const additionalInfoValue =
                isNaN(payoutNet) || payoutNet < 0
                    ? stringResolver.resolveString('$BETSLIP.NOT_AVAILABLE')
                    : CurrencyHelper.formatCurrency(payoutNet, currencyCode);

            return (
                <FlexPane
                    class={'ta-additionalInfoContainer'}
                    config={config.additionalInfoContainer}
                >
                    <Text class="ta-additionalInfoLabel" config={additionalInfoLabel}>
                        {stringResolver.resolveAndFormatString(
                            '$BETSLIP.POTENTIAL_RETURN_IN_PLACE_BET_BUTTON'
                        )}
                    </Text>
                    <Text class="ta-additionalInfoValue" config={config.additionalInfoValue}>
                        {additionalInfoValue}
                    </Text>
                </FlexPane>
            );
        } else if (isShareBetMode) {
            return (
                <FlexPane
                    class={'ta-additionalInfoContainer'}
                    config={config.additionalInfoContainer}
                >
                    <Text class="ta-additionalInfoLabel" config={additionalInfoLabel}>
                        {stringResolver.resolveAndFormatString('$BETSLIP.AFFILIATE_LABEL')}
                    </Text>
                    <Text class="ta-additionalInfoValue" config={config.additionalInfoValue}>
                        {affiliateId ||
                            stringResolver.resolveAndFormatString(
                                '$BETSLIP.AFFILIATE_NOT_AVAILABLE'
                            )}
                    </Text>
                </FlexPane>
            );
        }
        return null;
    };

    const { stake = 0 } = calculation;
    const totalStake = CurrencyHelper.formatCurrency(stake, currencyCode);

    return (
        <AnimatedButton
            config={config.buttonStyle}
            onClick={onClick}
            state={buttonState}
            class={`${className} ${classNameSuffix}`}
        >
            {config.showIcon && (
                <Image
                    class="ta-commitButtonIcon"
                    config={
                        buttonState === ANIMATED_BUTTON_STATES.DISABLED
                            ? style.iconDisabled
                            : config.icon
                    }
                />
            )}
            <FlexPane
                config={config.animatedButtonInfoContainer}
                class="ta-PlaceBetButtonInfoContainer"
            >
                <FlexPane config={config.textContainer} class="ta-PlaceBetButtonTextContainer">
                    <Text class="ta-PlaceBetButtonText" config={buttonTextConfig}>
                        {buttonText}
                    </Text>
                    {showStake && (
                        <Text class="ta-PlaceBetButtonStake" config={totalStakeTextConfig}>
                            {totalStake}
                        </Text>
                    )}
                </FlexPane>
                {renderAdditionalInfo()}
            </FlexPane>
            {showTooltip && (
                <TimedTooltip
                    config={config.tooltip}
                    onClose={onCloseTooltip}
                    class="ta-PlaceBetButtonTooltip"
                />
            )}
        </AnimatedButton>
    );
};

export default CommitBetslipButton;

CommitBetslipButton.getStyle = (config, applicationMode, merge) => {
    const { buttonText, totalStakeText, additionalInfoLabel, disabledText, icon, disabledIcon } =
        config;

    const disabledTextOverride = omit(disabledText, ['truncate']);

    return {
        buttonTextDisabled: merge(buttonText, disabledTextOverride),
        totalStakeTextDisabled: merge(totalStakeText, disabledTextOverride),
        additionalInfoDisabled: merge(additionalInfoLabel, disabledTextOverride),
        iconDisabled: merge(icon, disabledIcon),
    };
};
